import React, { useState, useEffect } from "react";
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";

// Reliable GeoJSON source (World Countries)
const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Mapping from French country names (DB) to English names (GeoJSON)
const frenchToEnglish = {
    "AFRIQUE DU SUD": "South Africa",
    "ALGERIE": "Algeria",
    "ALLEMAGNE": "Germany",
    "ANGOLA": "Angola",
    "ARABIE SAOUDITE": "Saudi Arabia",
    "ARGENTINE": "Argentina",
    "ARMENIE": "Armenia",
    "AUSTRALIE": "Australia",
    "AUTRICHE": "Austria",
    "BANGLADESH": "Bangladesh",
    "BELGIQUE": "Belgium",
    "BENIN": "Benin",
    "BOLIVIE": "Bolivia",
    "BRESIL": "Brazil",
    "BULGARIE": "Bulgaria",
    "BURUNDI": "Burundi",
    "CAMBODGE": "Cambodia",
    "CAMEROUN": "Cameroon",
    "CANADA": "Canada",
    "CAP-VERT": "Cape Verde",
    "CHILI": "Chile",
    "CHINE": "China",
    "CHYPRE": "Cyprus",
    "COLOMBIE": "Colombia",
    "COMORES": "Comoros",
    "CONGO": "Congo",
    "COREE DU SUD": "South Korea",
    "COSTA RICA": "Costa Rica",
    "COTE D'IVOIRE": "Ivory Coast",
    "CROATIE": "Croatia",
    "CUBA": "Cuba",
    "DANEMARK": "Denmark",
    "DJIBOUTI": "Djibouti",
    "EMIRATS ARABES UNIS": "United Arab Emirates",
    "EQUATEUR": "Ecuador",
    "ESPAGNE": "Spain",
    "ESTONIE": "Estonia",
    "ESWATINI / SWAZILAND": "eSwatini",
    "ETATS-UNIS": "United States of America",
    "ETHIOPIE": "Ethiopia",
    "FIDJI": "Fiji",
    "FINLANDE": "Finland",
    "GABON": "Gabon",
    "GHANA": "Ghana",
    "GRECE": "Greece",
    "GUINEE": "Guinea",
    "GUINEE EQUATORIALE": "Eq. Guinea",
    "GUYANA": "Guyana",
    "HONG KONG": "Hong Kong",
    "HONGRIE": "Hungary",
    "ILES SALOMON": "Solomon Is.",
    "INDE": "India",
    "INDONESIE": "Indonesia",
    "IRLANDE": "Ireland",
    "ISLANDE": "Iceland",
    "ITALIE": "Italy",
    "JAMAIQUE": "Jamaica",
    "JAPON": "Japan",
    "JORDANIE": "Jordan",
    "KAZAKHSTAN": "Kazakhstan",
    "KENYA": "Kenya",
    "KOWEIT": "Kuwait",
    "LAOS": "Laos",
    "LETTONIE": "Latvia",
    "LITUANIE": "Lithuania",
    "LUXEMBOURG": "Luxembourg",
    "MADAGASCAR": "Madagascar",
    "MALAISIE": "Malaysia",
    "MALTE": "Malta",
    "MAROC": "Morocco",
    "MAURICE": "Mauritius",
    "MAURITANIE": "Mauritania",
    "MEXIQUE": "Mexico",
    "MOLDAVIE": "Moldova",
    "MONGOLIE": "Mongolia",
    "NAMIBIE": "Namibia",
    "NIGERIA": "Nigeria",
    "NORVEGE": "Norway",
    "NOUVELLE-ZELANDE": "New Zealand",
    "OMAN": "Oman",
    "OUGANDA": "Uganda",
    "OUZBEKISTAN": "Uzbekistan",
    "PANAMA": "Panama",
    "PAPOUASIE-NOUVELLE-GUINEE": "Papua New Guinea",
    "PAYS-BAS": "Netherlands",
    "PEROU": "Peru",
    "PHILIPPINES": "Philippines",
    "POLOGNE": "Poland",
    "PORTUGAL": "Portugal",
    "QATAR": "Qatar",
    "REPUBLIQUE DEMOCRATIQUE DU CONGO": "Dem. Rep. Congo",
    "REPUBLIQUE DOMINICAINE": "Dominican Rep.",
    "ROUMANIE": "Romania",
    "ROYAUME-UNI": "United Kingdom",
    "RWANDA": "Rwanda",
    "SAINT-SIEGE": "Vatican",
    "SAINTE-LUCIE": "Saint Lucia",
    "SENEGAL": "Senegal",
    "SIERRA LEONE": "Sierra Leone",
    "SINGAPOUR": "Singapore",
    "SLOVAQUIE": "Slovakia",
    "SLOVENIE": "Slovenia",
    "SUEDE": "Sweden",
    "SUISSE": "Switzerland",
    "SURINAME": "Suriname",
    "TAIWAN": "Taiwan",
    "TANZANIE": "Tanzania",
    "TCHAD": "Chad",
    "TCHEQUIE / REPUBLIQUE TCHEQUE": "Czechia",
    "THAILANDE": "Thailand",
    "TOGO": "Togo",
    "TUNISIE": "Tunisia",
    "TURQUIE": "Turkey",
    "URUGUAY": "Uruguay",
    "VIET NAM": "Vietnam",
    "ZAMBIE": "Zambia",
};

// Build reverse mapping (English GeoJSON name → French DB name)
const englishToFrench = Object.fromEntries(
    Object.entries(frenchToEnglish).map(([fr, en]) => [en, fr])
);

const MapSection = () => {
    const navigate = useNavigate();
    const [tooltipContent, setTooltipContent] = useState("");
    const [isMapActive, setIsMapActive] = useState(false);
    const [offerCountries, setOfferCountries] = useState(new Set());

    // Fetch distinct countries that have active offers (via RPC for performance)
    useEffect(() => {
        const fetchOfferCountries = async () => {
            try {
                const { data, error } = await supabase.rpc('get_offer_countries');

                if (error) {
                    console.error("Error fetching offer countries:", error);
                    return;
                }

                if (data) {
                    const englishNames = new Set();
                    data.forEach(row => {
                        const en = frenchToEnglish[row.pays];
                        if (en) englishNames.add(en);
                    });
                    setOfferCountries(englishNames);
                }
            } catch (err) {
                console.error("Error fetching offer countries:", err);
            }
        };

        fetchOfferCountries();
    }, []);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                setIsMapActive(false);
            }
        };

        if (isMapActive) {
            window.addEventListener("keydown", handleKeyDown);
        }

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [isMapActive]);

    const handleCountryClick = (geo) => {
        if (!isMapActive) return;
        const englishName = geo.properties.name;
        if (!offerCountries.has(englishName)) return; // Grey countries not clickable
        const frenchName = englishToFrench[englishName] || englishName;
        navigate(`/offres_vie?country=${encodeURIComponent(frenchName)}`);
    };

    // Violet color matching CompaniesPage banner
    const VIOLET_FILL = "#8b5cf6";        // violet-500
    const VIOLET_HOVER = "#a78bfa";       // violet-400 (lighter on hover)
    const VIOLET_PRESSED = "#7c3aed";     // violet-600 (darker on press)

    return (
        <section className="w-full bg-[#0F172A] flex flex-col items-center justify-center overflow-hidden h-[750px] relative">

            {/* Text Header (Static) */}
            <div className="pt-12 pb-6 z-30 text-center px-4 relative">
                <h2 className="text-white text-4xl md:text-5xl font-black font-inter tracking-tight drop-shadow-2xl mb-4">
                    Explore le monde des possibles
                </h2>
                <p className="text-white/60 text-lg font-inter drop-shadow-lg max-w-2xl mx-auto">
                    Clique sur un pays pour découvrir les offres disponibles
                </p>
            </div>

            {/* Map Content Container */}
            <div className="w-full flex-grow relative group min-h-[500px]">

                {/* Unified Persistent Control Pill */}
                <div className="absolute top-8 left-0 right-0 z-[60] flex justify-center pointer-events-none">
                    <button
                        onClick={() => setIsMapActive(!isMapActive)}
                        className="pointer-events-auto bg-blue-400/10 backdrop-blur-sm px-8 py-3 rounded-full border border-blue-400/30 shadow-lg hover:bg-blue-400/20 transition-all duration-300 group flex items-center gap-3"
                    >
                        <p className={`text-blue-400 text-sm font-bold uppercase tracking-[0.2em] transition-opacity duration-300 ${isMapActive ? 'animate-pulse' : ''}`}>
                            {isMapActive ? 'Appuyez sur "ÉCHAP" pour verrouiller' : 'Cliquez sur la carte pour activer'}
                        </p>
                        <div className={`w-2 h-2 rounded-full shadow-[0_0_10px_currentColor] transition-colors duration-300 ${isMapActive ? 'bg-green-500 text-green-500' : 'bg-red-500 text-red-500'}`} />
                    </button>
                </div>

                {/* Map - Always Clear */}
                <div className={`w-full h-full transition-all duration-500 ${!isMapActive ? 'pointer-events-none' : ''}`}>
                    <ComposableMap projection="geoMercator" projectionConfig={{ scale: 140 }} style={{ width: "100%", height: "100%" }}>
                        <ZoomableGroup center={[0, 20]} zoom={1}>
                            <Geographies geography={geoUrl}>
                                {({ geographies }) =>
                                    geographies.map((geo) => {
                                        const isAntarctica = geo.properties.name === "Antarctica";
                                        if (isAntarctica) return null;

                                        const countryName = geo.properties.name;
                                        const hasOffers = offerCountries.has(countryName);

                                        return (
                                            <Geography
                                                key={geo.rsmKey}
                                                geography={geo}
                                                onClick={() => handleCountryClick(geo)}
                                                onMouseEnter={() => {
                                                    if (isMapActive) setTooltipContent(countryName);
                                                }}
                                                onMouseLeave={() => {
                                                    setTooltipContent("");
                                                }}
                                                style={{
                                                    default: {
                                                        fill: hasOffers ? VIOLET_FILL : "#334155",
                                                        stroke: hasOffers ? "#a78bfa" : "#64748B",
                                                        strokeWidth: hasOffers ? 0.9 : 0.7,
                                                        outline: "none",
                                                        transition: "all 250ms"
                                                    },
                                                    hover: {
                                                        fill: hasOffers ? VIOLET_HOVER : "#475569",
                                                        stroke: "#FFFFFF",
                                                        strokeWidth: 1.5,
                                                        outline: "none",
                                                        cursor: isMapActive && hasOffers ? "pointer" : "default"
                                                    },
                                                    pressed: {
                                                        fill: hasOffers ? VIOLET_PRESSED : "#1E293B",
                                                        stroke: "#FFFFFF",
                                                        outline: "none"
                                                    }
                                                }}
                                            />
                                        );
                                    })
                                }
                            </Geographies>
                        </ZoomableGroup>
                    </ComposableMap>
                </div>

                {/* Invisible Click Capture Layer (Only when inactive) */}
                {!isMapActive && (
                    <div
                        onClick={() => setIsMapActive(true)}
                        className="absolute inset-0 z-50 cursor-pointer"
                        aria-label="Activate map"
                    />
                )}

                {/* Gradients (Inside map container) */}
                <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-[#0F172A] to-transparent pointer-events-none z-10" />
                <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-[#0F172A] to-transparent pointer-events-none z-10" />

                {/* Tooltip */}
                {tooltipContent && isMapActive && (
                    <div className="absolute bottom-12 left-1/2 -translate-x-1/2 bg-white text-slate-900 px-6 py-3 rounded-full font-black shadow-2xl pointer-events-none animate-in fade-in slide-in-from-bottom-2 z-[70] border-2 border-blue-500">
                        {tooltipContent}
                    </div>
                )}
            </div>
        </section>
    );
};

export default MapSection;
